import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Box, Stack, Text, Flex, Badge, Button, Image } from "@chakra-ui/react";
import { RotateCcw, Cloud } from "lucide-react";
import { ConditionBuilder } from "components/ConditionBuilder";
import { createDefaultConditionGroup } from "components/ConditionBuilder/utils/conditionUtils";
import {
  EnvironmentalVariablesLoadingSkeleton,
  EnvironmentalVariablesErrorState,
} from "components/ConditionBuilder/components";
import i4castLogoSmall from "assets/i4cast-logo-small.svg";
import {
  AlertConfig,
  ConditionGroup,
} from "stores/data/insightAlerts/insightAlertModel";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { CreateAlertFormValues } from "../types";
import { AlertTypeSelector, RISK_TYPES, RiskTypeId } from "./AlertTypeSelector";
import { useStores } from "stores/setup/use-store";
import { EventActionSelector } from "pages/Event/components/EventActionSelector";
import { ConditionsEmptyState } from "pages/Event/components/ConditionsEmptyState";
import { SensorAlertSection } from "components/SensorAlertSection/SensorAlertSection";
import { SensorAlertConfig } from "types/ISensorAlertConfig";

interface Step2AlertTypeConditionsProps {
  t: (key: string) => string;
  values: CreateAlertFormValues;
  selectedTemplate?: AlertConfig | null;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  environmentalVariables?: IEnvironmentalVariable[];
  errors: any;
  touched?: any;
  /** Whether to show the alert type selector. Defaults to true (for events). Set to false for alerts. */
  showAlertTypeSelector?: boolean;
  /** Initial template conditions for edit mode - allows showing badge/reset button for existing events */
  initialTemplateConditions?: ConditionGroup[] | null;
  /** Current restriction type value - true = Restrictive, false = Permissive. Only used for Events. */
  restrictionType?: boolean;
  /** Callback when restriction type changes. Only used for Events. */
  onRestrictionTypeChange?: (value: boolean) => void;
}

export const Step2AlertTypeConditions: React.FC<Step2AlertTypeConditionsProps> =
  observer(({
    t,
    values,
    setFieldValue,
    environmentalVariables,
    errors,
    touched,
    showAlertTypeSelector = true,
    initialTemplateConditions,
    restrictionType,
    onRestrictionTypeChange,
  }) => {
    const {
      dataStores: { insightAlertStore },
    } = useStores();

    // Read original template conditions from MobX store (persists across drawer remount)
    const originalTemplateConditions =
      insightAlertStore.originalTemplateConditions;

    // Initialize originalTemplateConditions from prop (for edit mode)
    useEffect(() => {
      if (initialTemplateConditions && !originalTemplateConditions) {
        // Deep clone to prevent reference mutation
        const clonedConditions = JSON.parse(
          JSON.stringify(initialTemplateConditions),
        );
        insightAlertStore.setOriginalTemplateConditions(clonedConditions);
      }
    }, [
      initialTemplateConditions,
      originalTemplateConditions,
      insightAlertStore,
    ]);

    const conditionGroups = useMemo((): ConditionGroup[] => {
      if (!values.conditionGroups || values.conditionGroups.length === 0) {
        return [createDefaultConditionGroup()];
      }
      return values.conditionGroups;
    }, [values.conditionGroups]);

    /**
     * Callback when template conditions are applied from AlertTypeSelector
     * Stores the original conditions for comparison (deep clone to avoid reference issues)
     */
    const handleTemplateConditionsApplied = useCallback(
      (conditions: ConditionGroup[]) => {
        // Deep clone to prevent reference mutation when user modifies conditions
        const clonedConditions = JSON.parse(JSON.stringify(conditions));
        insightAlertStore.setOriginalTemplateConditions(clonedConditions);
      },
      [insightAlertStore],
    );

    /**
     * Normalize conditions for comparison by removing IDs and sorting
     * This ensures we compare only the actual condition values
     * Operators are normalized to lowercase for case-insensitive comparison
     */
    const normalizeConditionsForComparison = useCallback(
      (groups: ConditionGroup[] | null | undefined): string => {
        if (!groups || groups.length === 0) return "";

        const normalized = groups.map((group) => ({
          groupOperator: (group.groupOperator || "and").toLowerCase(),
          conditionOperator: (group.conditionOperator || "and").toLowerCase(),
          conditions: [...group.conditions]
            .sort((a, b) => {
              // Sort by environmentalVariableId first, then by value
              if (a.environmentalVariableId !== b.environmentalVariableId) {
                return a.environmentalVariableId - b.environmentalVariableId;
              }
              return a.value - b.value;
            })
            .map((condition) => ({
              environmentalVariableId: condition.environmentalVariableId,
              comparisonOperator: condition.comparisonOperator,
              value: condition.value,
            })),
        }));

        return JSON.stringify(normalized);
      },
      [],
    );

    /**
     * Check if current conditions match the original template conditions
     * Uses normalized comparison that ignores IDs and property order
     */
    const conditionsMatchTemplate = useMemo((): boolean => {
      if (!originalTemplateConditions) {
        return false;
      }
      const currentNormalized = normalizeConditionsForComparison(
        values.conditionGroups,
      );
      const originalNormalized = normalizeConditionsForComparison(
        originalTemplateConditions,
      );

      return currentNormalized === originalNormalized;
    }, [
      values.conditionGroups,
      originalTemplateConditions,
      normalizeConditionsForComparison,
    ]);

    /**
     * Check if there's a template available for the current risk type
     * In edit mode (when initialTemplateConditions is provided), we know a template exists
     */
    const hasTemplateForCurrentRisk = useMemo((): boolean => {
      // In edit mode, if we received initial template conditions, a template exists
      if (initialTemplateConditions && initialTemplateConditions.length > 0) {
        return true;
      }

      const templateType = values.templateType;
      if (!templateType) return false;

      const risk = RISK_TYPES[templateType as RiskTypeId];
      if (!risk?.backendKey) return false;

      const macroRegion = insightAlertStore.macroRegionOrigin;
      if (!macroRegion) return false;

      const template = insightAlertStore.getTemplateForRisk(
        risk.backendKey,
        macroRegion,
      );
      return !!template?.conditions;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      values.templateType,
      insightAlertStore.macroRegionOrigin,
      insightAlertStore.customAlertTemplates,
      insightAlertStore.defaultAlertTemplates,
      initialTemplateConditions,
    ]);

    /**
     * Reset conditions to the original template
     */
    const handleResetToTemplate = useCallback(() => {
      if (originalTemplateConditions) {
        setFieldValue("conditionGroups", originalTemplateConditions, false);
      }
    }, [originalTemplateConditions, setFieldValue]);

    const isNoCategory = values.templateType === "no_category";

    // Determine if we should show the badge (conditions match template) — never for "Sem categoria" or Permissivo
    const showI4castBadge =
      restrictionType !== false &&
      !isNoCategory &&
      conditionsMatchTemplate &&
      hasTemplateForCurrentRisk;

    // Determine if we should show the reset button — never for "Sem categoria" or Permissivo
    const showResetButton =
      restrictionType !== false &&
      !isNoCategory &&
      !conditionsMatchTemplate &&
      originalTemplateConditions !== null;

    // Determine if we should show the risk category selector
    // Only show when restrictionType is true (Restrictive) or when restrictionType is not provided (Alerts)
    const shouldShowRiskCategorySelector =
      restrictionType === undefined || restrictionType === true;

    /**
     * Determine if we should show the conditions empty state.
     * Shown when conditionGroups is empty (no conditions set yet).
     * Only for Events (when onRestrictionTypeChange is provided).
     * When user selects a risk type, template conditions populate conditionGroups → empty state hides.
     * When user clicks "Criar condição", we write a default group → empty state hides.
     * If user already has conditions and switches back to "Sem categoria", conditionGroups stays populated.
     */
    const shouldShowConditionsEmptyState =
      onRestrictionTypeChange !== undefined &&
      (!values.conditionGroups || values.conditionGroups.length === 0);

    /** Dismiss the empty state by creating a default condition group */
    const handleDismissEmptyState = useCallback(() => {
      setFieldValue("conditionGroups", [createDefaultConditionGroup()], false);
    }, [setFieldValue]);

    // Ref and scroll effect for condition error message
    const conditionErrorRef = useRef<HTMLParagraphElement>(null);
    const showConditionError = !!(
      errors?.conditionGroups && touched?.conditionGroups
    );

    useEffect(() => {
      if (showConditionError && conditionErrorRef.current) {
        conditionErrorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [showConditionError]);

    return (
      <Stack spacing="32px">
        {/* Event Action Selector - Only shown for events (when onRestrictionTypeChange is provided) */}
        {onRestrictionTypeChange && restrictionType !== undefined && (
          <EventActionSelector
            value={restrictionType}
            onChange={onRestrictionTypeChange}
            t={t}
          />
        )}

        {/* Alert Type Selector - Only shown for events when Restrictive is selected */}
        {showAlertTypeSelector && shouldShowRiskCategorySelector && (
          <AlertTypeSelector
            t={t}
            values={values}
            setFieldValue={setFieldValue}
            onTemplateConditionsApplied={handleTemplateConditionsApplied}
            environmentalVariables={environmentalVariables}
            originalTemplateConditions={originalTemplateConditions}
            errors={errors}
            touched={touched}
          />
        )}

        {/* Conditions Section with Border */}
        <Box border="1px solid #D4D4D8" borderRadius="8px" padding="16px">
          {/* Alert Conditions Header with Badge/Reset */}
          <Flex align="center" justify="space-between" mb="28px">
            <Flex align="center" gap={2}>
              <Cloud size={20} color="#086AA9" />
              <Text fontWeight="500" fontSize="16px" color="#086AA9">
                {t("alertConditions")}
              </Text>
              {showI4castBadge && (
                <Badge
                  bg="#DBF1FE"
                  color="#0884cc"
                  fontSize="12px"
                  fontWeight="400"
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  px={2}
                  py="2px"
                  height="22px"
                  borderRadius="4px"
                  textTransform="none"
                >
                  <Image
                    src={i4castLogoSmall}
                    alt="i4cast"
                    width="14px"
                    height="14px"
                  />
                  {t("i4castSuggestion")}
                </Badge>
              )}
            </Flex>
            <Flex align="center" gap={2}>
              {showResetButton && (
                <Button
                  variant="outline"
                  bg="transparent"
                  borderColor="#086AA9"
                  color="#086AA9"
                  fontSize="14px"
                  fontWeight="400"
                  height="32px"
                  px={4}
                  borderRadius="8px"
                  leftIcon={<RotateCcw size={16} />}
                  onClick={handleResetToTemplate}
                  _hover={{ bg: "rgba(8, 106, 169, 0.1)" }}
                >
                  {t("backToI4castSuggestion")}
                </Button>
              )}
            </Flex>
          </Flex>

          {insightAlertStore.isLoadingEnvironmentalVariables ? (
            <EnvironmentalVariablesLoadingSkeleton />
          ) : insightAlertStore.hasErrorLoadingEnvironmentalVariables ? (
            <EnvironmentalVariablesErrorState
              errorMessage={t("errorLoadingEnvironmentalVariables")}
              retryLabel={t("retryLoadEnvVariables")}
              onRetry={() =>
                insightAlertStore.retryLoadEnvironmentalVariables()
              }
            />
          ) : shouldShowConditionsEmptyState ? (
            <ConditionsEmptyState
              onCreateCondition={handleDismissEmptyState}
              t={t}
              showRiskSuggestion={restrictionType !== false}
            />
          ) : (
            <Box bg="#F4F4F5" padding="24px" borderRadius={8}>
              <ConditionBuilder
                conditionGroups={conditionGroups}
                environmentalVariables={environmentalVariables}
                setFieldValue={setFieldValue}
                t={t}
                errors={errors}
              />
            </Box>
          )}

          {/* Error message when no conditions are added */}
          {showConditionError && (
            <Text
              ref={conditionErrorRef}
              color="red.500"
              fontSize="sm"
              mt="8px"
            >
              {t("pleaseAddCondition")}
            </Text>
          )}
        </Box>

        {/* Sensor Alert Section - Only shown for events */}
        {onRestrictionTypeChange && (
          <SensorAlertSection
            locationPoint={
              insightAlertStore.selectedStationForAlert
                ? {
                    lat: insightAlertStore.selectedStationForAlert.station_lat,
                    lng: insightAlertStore.selectedStationForAlert.station_lon,
                  }
                : undefined
            }
            geofence={
              insightAlertStore.selectedGeofence?.length > 0
                ? insightAlertStore.selectedGeofence
                : undefined
            }
            macroRegionId={values.macroRegionId}
            macroRegionOrigin={insightAlertStore.macroRegionOrigin ?? undefined}
            setFieldValue={setFieldValue}
            sensorConditions={
              (
                values as CreateAlertFormValues & {
                  sensorAlertConfig?: SensorAlertConfig;
                }
              ).sensorAlertConfig?.conditions ?? []
            }
            alertFrequency={
              (
                values as CreateAlertFormValues & {
                  sensorAlertConfig?: SensorAlertConfig;
                }
              ).sensorAlertConfig?.alertFrequency ?? 60
            }
            initialSensorAlertConfig={
              (
                values as CreateAlertFormValues & {
                  sensorAlertConfig?: SensorAlertConfig;
                }
              ).sensorAlertConfig
            }
            editModeEventId={
              (values as CreateAlertFormValues & { eventInsightId?: number })
                .eventInsightId
            }
            eventName={(values as CreateAlertFormValues & { name?: string }).name}
            eventLocationName={(values as CreateAlertFormValues & { area?: string }).area}
            errors={errors}
            touched={touched}
          />
        )}
      </Stack>
    );
  });
